/**
 * Box Plot Chart Type Definition
 */

import {
  paletteControl,
  gridControl,
  titleControl,
  xLabelControl,
  yLabelControl,
  yMinControl,
  yMaxControl,
  labelRotationControl,
  truncateXAxisLabelsControl,
} from '../shared/controls';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  gridCode,
  axisLabelsCode,
  yAxisRangeCode,
  spineCode,
  finishCode,
} from '../shared/codeSnippets';
import { getColors } from '../shared/palettes';

const boxPlot = {
  id: 'box-plot',
  name: 'Box Plot',
  category: 'distribution',
  description: 'Compare distributions and identify outliers',
  
  sampleData: [
    { category: 'Group A', values: [82, 86, 89, 91, 93, 94, 95, 95, 96, 97, 98, 99, 99], median: 95, q1: 90, q3: 97, min: 82, max: 99, outliers: [] },
    { category: 'Group B', values: [78, 82, 85, 88, 90, 91, 92, 93, 94, 95, 96, 97, 98], median: 92, q1: 87, q3: 95, min: 78, max: 98, outliers: [] },
    { category: 'Group C', values: [75, 80, 84, 87, 89, 90, 91, 92, 93, 94, 95, 96, 65], median: 90, q1: 84, q3: 94, min: 75, max: 96, outliers: [65] },
    { category: 'Group D', values: [70, 74, 78, 81, 84, 86, 87, 88, 90, 91, 93, 95, 97], median: 87, q1: 80, q3: 92, min: 70, max: 97, outliers: [] },
  ],
  
  defaultConfig: {
    useBluesPalette: true,
    useRedsPalette: false,
    useGreensPalette: false,
    showGrid: true,
    showOutliers: true,
    boxWidth: 0.4,
    ylabel: 'Value',
    xlabel: 'Category',
    title: '',
    yMin: '',
    yMax: '',
    labelRotation: null,
    truncateXAxisLabels: false,
  },
  
  controls: [
    paletteControl,
    {
      key: 'showOutliers',
      label: 'Show Outliers',
      type: 'toggle',
    },
    {
      key: 'boxWidth',
      label: 'Box Width',
      type: 'slider',
      min: 0.3,
      max: 0.9,
      step: 0.1,
      unit: '',
    },
    gridControl,
    labelRotationControl,
    truncateXAxisLabelsControl,
    xLabelControl,
    yLabelControl,
    titleControl,
    yMinControl,
    yMaxControl,
  ],
  
  generateCode: (config, dataContext, s) => {
    const hasUserData = dataContext?.isLoaded && dataContext.mapping?.category && dataContext.mapping?.value;
    const colors = JSON.stringify(getColors(config, 4, s));

    const showOutliersCode = config.showOutliers 
      ? `showfliers=True, flierprops=dict(marker='*', markersize=8, markerfacecolor='${s?.foreground || '#000000'}', markeredgecolor='${s?.foreground || '#000000'}')`
      : "showfliers=False";

    return `${matplotlibSetup(s)}

# ======== DATA ========
${hasUserData ? `df = pd.read_csv('${dataContext.fileName}')
# Group values by category
data = {str(name): group['${dataContext.mapping.value}'].dropna().tolist() for name, group in df.groupby('${dataContext.mapping.category}')}` : `data = {
    'GPT-4': [82, 86, 89, 91, 93, 94, 95, 95, 96, 97, 98, 99, 99],  # Category 1 values
    'Claude': [78, 82, 85, 88, 90, 91, 92, 93, 94, 95, 96, 97, 98],  # Category 2 values
    'Gemini': [75, 80, 84, 87, 89, 90, 91, 92, 93, 94, 95, 96, 65],  # Category 3 (with outlier)
    'LLaMA': [70, 74, 78, 81, 84, 86, 87, 88, 90, 91, 93, 95, 97]  # Category 4 values
}`}
# ======================

# Prepare data for box plot
models = list(data.keys())
values = list(data.values())
${createFigure(5.5, 4.5, 300, s)}

# Define colors for each box
colors = ${colors}

# Create box plot
bp = ax.boxplot(values,
               labels=models,
               patch_artist=True,
               widths=${config.boxWidth || 0.4},
               ${showOutliersCode},
               medianprops=dict(color='${s?.foreground || '#000000'}', linewidth=2),
               boxprops=dict(linewidth=1.5, edgecolor='${s?.axisColor || '#000000'}'),
               whiskerprops=dict(color='${s?.axisColor || '#000000'}', linewidth=1.5),
               capprops=dict(color='${s?.axisColor || '#000000'}', linewidth=1.5))

# Color each box
for patch, color in zip(bp['boxes'], colors):
    patch.set_facecolor(color)
${titleCode(config, s)}
${axisLabelsCode(config, s)}

# Add grid
${gridCode(config, 'y', s)}

# Set y-axis range
${yAxisRangeCode(config)}
${spineCode(s)}
${finishCode('box_plot')}`;
  },
};

export default boxPlot;
